/**
 * Partage du fichier image : natif (Capacitor) → Web Share avec fichier →
 * téléchargement. L'annulation depuis la feuille système n'est PAS une erreur
 * (Web Share et Capacitor lèvent une exception à la fermeture) : on la distingue
 * pour laisser la modale ouverte sans message d'échec.
 */

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

export type ShareImageOutcome = 'shared' | 'downloaded' | 'cancelled' | 'failed';

const FILE_NAME = 'gazette-evenement.png';

export interface ShareImageInput {
  blob: Blob;
  caption: string;
  title: string;
}

/** Lien vers la fiche dans l'app, pour la légende. */
export function eventShareUrl(eventId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/event/${eventId}`;
}

function isAbort(error: unknown): boolean {
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.name === 'NotAllowedError') return true;
    return /cancel|abort|dismiss/i.test(error.message);
  }
  return false;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('lecture du blob impossible'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

function downloadBlob(blob: Blob): boolean {
  try {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = FILE_NAME;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return true;
  } catch {
    return false;
  }
}

async function shareNative({ blob, caption, title }: ShareImageInput): Promise<ShareImageOutcome> {
  let uri: string;
  try {
    const data = await blobToBase64(blob);
    const written = await Filesystem.writeFile({ path: FILE_NAME, data, directory: Directory.Cache });
    uri = written.uri;
  } catch {
    return 'failed';
  }
  try {
    await Share.share({ title, text: caption, files: [uri], dialogTitle: title });
    return 'shared';
  } catch {
    // Sur mobile, une exception à ce stade = annulation utilisateur la plupart
    // du temps : ne jamais afficher d'échec, laisser l'utilisateur retenter.
    return 'cancelled';
  } finally {
    void Filesystem.deleteFile({ path: FILE_NAME, directory: Directory.Cache }).catch(() => {});
  }
}

async function shareWeb({ blob, caption, title }: ShareImageInput): Promise<ShareImageOutcome> {
  const file = new File([blob], FILE_NAME, { type: 'image/png' });
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const canShareFile = nav?.share && nav.canShare?.({ files: [file] });
  if (canShareFile) {
    try {
      await nav.share({ files: [file], text: caption, title });
      return 'shared';
    } catch (error) {
      if (isAbort(error)) return 'cancelled';
      /* autre erreur → repli sur le téléchargement */
    }
  }
  return downloadBlob(blob) ? 'downloaded' : 'failed';
}

/**
 * Partage l'image. Renvoie comment elle a quitté l'app :
 * 'shared' (feuille système), 'downloaded' (repli desktop, feedback explicite),
 * 'cancelled' (annulation — pas une erreur), 'failed' (échec réel).
 */
export async function shareEventImage(input: ShareImageInput): Promise<ShareImageOutcome> {
  if (Capacitor.isNativePlatform()) return shareNative(input);
  return shareWeb(input);
}
